import React from "react";
import { JSONTree } from "react-json-tree";
import greenCircle from "../../../assets/images/greenCircle.svg";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import { ListSubheader, Typography } from "@mui/material";
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { parseFontCheck, getStatusIcon, getStatusColor } from './utils/MetadataUtils';
import FontCheckResult from './FontCheckResult';
import StatusRow from './utils/StatusRow';
import { renderCreateModifiedDates } from './utils/DateUtils';
import { getFurtherVerificationDisplay, splitMetadataRule } from './utils/metadataRuleUtils';

const Dark = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

const formatDate = (year: string, month: string, day: string) => {
  return `${month.padStart(2, '0')}-${day.padStart(2, '0')}-${year.slice(-4)}`;
};

const formatTime = (time: string) => {
  const [hours, minutes, seconds] = time.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
};

const checkCreateandModifiedDate = (dateString: string) => {
  if (dateString === 'Create Date should be Null') {
    return dateString;
  }
  if(dateString == 'Create Date should be Present'){
    return dateString
  }
  const normalizeDisplay = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return 'null';
    return trimmed;
  };

  const formatParts = (value: string) => {
    const normalized = normalizeDisplay(value);
    const [datePart = '', timePart = ''] = normalized.split(/\s+/);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && timePart) {
      const [year, month, day] = datePart.split('-');
      return {
        text: `${formatDate(year || '', month || '', day || '')} ${formatTime(timePart)}`,
      };
    }
    return { text: normalized };
  };

  const renderRow = (label: string, value: string) => {
    const formatted = formatParts(value).text || 'null';
    return (
      <div className="d-flex justify-content-start">
        <div style={{ minWidth: '90px' }}>{label}</div>
        <div className="px-1">{formatted}</div>
      </div>
    );
  };

  const hasCreate = /Create Date:/i.test(dateString);
  const hasModified = /Modified Date:/i.test(dateString);

  if (hasCreate && hasModified) {
    const [beforeModified, afterModified] = dateString.split(/Modified Date:/i);
    const createdVal = (beforeModified.split(/Create Date:/i).pop() || '').trim();
    const modifiedVal = (afterModified || '').trim();
    return (
      <>
        {renderRow('Create Date:', createdVal)}
        {renderRow('Modified Date:', modifiedVal)}
      </>
    );
  }

  const createdMatch = dateString.match(/Create Date:\s*([^\r\n]*)/i);
  const modifiedMatch = dateString.match(/Modified Date:\s*([^\r\n]*)/i);

  if (createdMatch || modifiedMatch) {
    return (
      <>
        {createdMatch && renderRow('Create Date:', createdMatch[1] || '')}
        {modifiedMatch && renderRow('Modified Date:', modifiedMatch[1] || '')}
      </>
    );
  }

  return dateString;
};



const NewMetaDataRulesBank = ({
  metaDataCheck
}) => {
  const LABEL_WIDTH = 160;
  const LABEL_MARGIN_RIGHT = 24;
  const STATUS_WIDTH = 100;
  const STATUS_MARGIN_RIGHT = 3;
  const labelStyle = { fontSize: '10px', minWidth: `${LABEL_WIDTH}px`, maxWidth: `${LABEL_WIDTH}px`, marginRight: `${LABEL_MARGIN_RIGHT}px` };
  const statusStyle = { fontSize: '9px', minWidth: `${STATUS_WIDTH}px`, maxWidth: '120px', marginRight: `${STATUS_MARGIN_RIGHT}px` };
  const formatStatusText = (status: string) => status === 'NA' ? 'No Rule' : status;
  return (<><div className="row" >
    {metaDataCheck.map((item, index) => {
      return (
        <div className="col-12 col-lg-12 col-md-12 mb-4" key={item?.filename || item?.source_id || index}>
          <div className="">
            <div className="border-bottom small mb-2"  style={{ fontSize: '10px' }}><strong>{index + 1}) {item?.filename?.split('_')?.pop()}</strong></div >
            
            {item.metaDataCheck.map((item, ruleIndex) => {
              const { ruleName: rawRuleName, ruleValue } = splitMetadataRule(item.rule);
              const ruleName = rawRuleName === 'File Size'
                ? 'File Size Check'
                : rawRuleName === 'Further Verification'
                  ? 'Further Verification Check'
                  : rawRuleName;
              const { actualFonts, requiredFonts, optionalFonts, expectedUnknown } = ruleName === 'Font Check' ? parseFontCheck(ruleValue) : { actualFonts: [], requiredFonts: [], optionalFonts: [], expectedUnknown: false };
              const actualCounts = actualFonts.reduce((acc, font) => {
                acc[font] = (acc[font] || 0) + 1;
                return acc;
              }, {});
              const originalActualCounts = { ...actualCounts };

              const parsedFonts = ruleName === 'Font Check' ? parseFontCheck(ruleValue) : null;
              const statusIcon = getStatusIcon(item.status);
              const statusColor = getStatusColor(item.status);
              const statusText = formatStatusText(item.status);
              const furtherVerificationDisplay = getFurtherVerificationDisplay(item.status, ruleValue);

              const isFontCheck = ruleName == 'Font Check';
              const rowStyle = { flexWrap: isFontCheck ? 'nowrap' : 'wrap' } as const;
              const fontCheckContentStyle: React.CSSProperties = {
                fontSize: '10px',
                minWidth: '200px',
                flex: '1 1 0',
                minHeight: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap',
              };

              return (<div className="d-flex align-items-start py-1" style={rowStyle} key={`${ruleName}-${ruleIndex}`}>
                <div style={labelStyle}>{ruleName}</div>
                <div className="d-flex align-items-center" style={statusStyle}>
                  <strong>{statusText}</strong>
                </div>
                {isFontCheck && parsedFonts && <div className="flex-grow-1" style={fontCheckContentStyle}>
                  <FontCheckResult parsedFonts={parsedFonts} rawRule={ruleValue} />
                </div>}
                {!isFontCheck && <div className="flex-grow-1" style={{ fontSize: '10px', minWidth: '200px' }}>
                  <StatusRow statusIcon={statusIcon} color={statusColor}>
                    {ruleName=='Further Verification Check'?furtherVerificationDisplay:renderCreateModifiedDates(ruleValue)}
                  </StatusRow>
                </div>}
              </div>)
            })}
          </div>
        </div>
      );
    })}
  </div></>)
};

export default NewMetaDataRulesBank;
